// The pure part of the Pay Rates page (payRateMargin.js): how each craft's base
// wage has moved year to year, and what the selected job's gross margin would
// be if its crew were paid an earlier year's rates.
//
// Input is readPayRateHistory's answer:
//   company  [{ craft, yr, q, hrs, pay }]  regular time, every job, by quarter;
//            pay ÷ hrs is the hours-weighted base rate
//   jobRows  [{ craft, yr, q, pt, hrs, gross, pay }]  the job's own wage rows
//            (1 regular, 5 overtime, 7 salaried overtime)
//
// How a job's labor is re-priced at an earlier year (the "rate index" method):
// each quarter's wages on the job, per craft, are scaled by how far that
// craft's company-wide base rate has moved between the chosen year and the
// quarter the hours were worked:
//     wages at year B = wages paid × rate(craft, B) ÷ rate(craft, quarter)
// So the job keeps its own crew and its own premium or discount to the company
// average; only the rise in rates since year B comes out. Overtime is a fixed
// 1.5 × base, so it scales the same way. A craft with too few hours company-wide
// to rate (MIN_HRS) falls back to the whole company's rate change, measured on
// the same craft mix so a shift in who was hired does not read as a raise.

export const MIN_HRS = 200;
export const WAGE_TYPES = [1, 5, 7];

const key2 = (c, y) => `${c}|${y}`;
const key3 = (c, y, q) => `${c}|${y}|${q}`;
const add = (m, k, hrs, pay) => {
    const cur = m.get(k) || { hrs: 0, pay: 0 };
    cur.hrs += hrs; cur.pay += pay;
    m.set(k, cur);
};
const rateOf = (cell) => (cell && cell.hrs >= MIN_HRS && cell.pay > 0 ? cell.pay / cell.hrs : null);

/**
 * The company rows with this job's own regular time taken out, so the job is
 * measured against the rest of the company and not against itself (a large job
 * can be most of a craft's hours in a quarter).
 */
export function companyWithout(company = [], jobRows = []) {
    const mine = new Map();
    jobRows.forEach((r) => {
        if (Number(r.pt) !== 1) return;
        add(mine, key3(String(r.craft || ''), r.yr, r.q), Number(r.hrs) || 0, Number(r.pay) || 0);
    });
    return company.map((r) => {
        const m = mine.get(key3(String(r.craft || ''), r.yr, r.q));
        if (!m) return r;
        return { ...r, hrs: Math.max(0, (Number(r.hrs) || 0) - m.hrs), pay: Math.max(0, (Number(r.pay) || 0) - m.pay) };
    });
}

export const craftLabel = (crafts, code) => (code ? (crafts?.[code] || `Craft ${code}`) : 'No craft in JDE');

/** Company base rates, indexed for lookup. */
export function buildRates(company = []) {
    const byYear = new Map();     // craft|yr -> { hrs, pay }
    const byQuarter = new Map();  // craft|yr|q -> { hrs, pay }
    const periodCrafts = new Map(); // yr|q -> Set(craft)
    const yearCrafts = new Map(); // yr -> Set(craft)
    const years = new Set();
    let last = null; // the latest { yr, q } with any hours
    company.forEach((r) => {
        const hrs = Number(r.hrs) || 0;
        const pay = Number(r.pay) || 0;
        if (hrs <= 0) return;
        const c = String(r.craft || '');
        add(byYear, key2(c, r.yr), hrs, pay);
        add(byQuarter, key3(c, r.yr, r.q), hrs, pay);
        const pk = `${r.yr}|${r.q}`;
        if (!periodCrafts.has(pk)) periodCrafts.set(pk, new Set());
        periodCrafts.get(pk).add(c);
        if (!yearCrafts.has(r.yr)) yearCrafts.set(r.yr, new Set());
        yearCrafts.get(r.yr).add(c);
        years.add(Number(r.yr));
        if (!last || r.yr > last.yr || (r.yr === last.yr && r.q > last.q)) last = { yr: Number(r.yr), q: Number(r.q) };
    });

    const yearRate = (c, y) => rateOf(byYear.get(key2(c, y)));
    const yearHours = (c, y) => byYear.get(key2(c, y))?.hrs || 0;
    /** A quarter's rate; a thin quarter borrows its year's. */
    const periodRate = (c, y, q) => rateOf(byQuarter.get(key3(c, y, q))) ?? yearRate(c, y);

    // The whole company's move from year B to a quarter, on that quarter's craft
    // mix: Σ hrs × rate(B) ÷ Σ hrs × rate(quarter), over crafts rated in both.
    const indexCache = new Map();
    const companyFactor = (b, y, q) => {
        const k = `${b}|${y}|${q}`;
        if (indexCache.has(k)) return indexCache.get(k);
        let then = 0; let now = 0;
        (periodCrafts.get(`${y}|${q}`) || []).forEach((c) => {
            const rb = yearRate(c, b);
            const rp = periodRate(c, y, q);
            const h = byQuarter.get(key3(c, y, q))?.hrs || 0;
            if (rb && rp && h) { then += h * rb; now += h * rp; }
        });
        const f = now > 0 ? then / now : 1;
        indexCache.set(k, f);
        return f;
    };

    /** { f, src }: wages worked in (y, q) × f = the same work at year B's rates. */
    const factor = (c, b, y, q) => {
        const rb = yearRate(c, b);
        const rp = periodRate(c, y, q);
        if (rb && rp) return { f: rb / rp, src: 'craft' };
        return { f: companyFactor(b, y, q), src: 'company' };
    };

    /** The company's year-over-year rise on one year's craft mix: Σ h(y) r(y) ÷ Σ h(y) r(b). */
    const companyChange = (b, y) => {
        let atY = 0; let atB = 0;
        (yearCrafts.get(y) || []).forEach((c) => {
            const ry = yearRate(c, y);
            const rb = yearRate(c, b);
            const h = yearHours(c, y);
            if (ry && rb && h) { atY += h * ry; atB += h * rb; }
        });
        return atB > 0 ? atY / atB - 1 : null;
    };

    return { years: [...years].sort((a, b) => a - b), last, yearRate, yearHours, periodRate, factor, companyFactor, companyChange };
}

/** The job's wage rows summed per craft: { craft, hrs, stHrs, otHrs, gross, basePay, baseHrs, rate }. */
export function jobCrafts(jobRows = []) {
    const m = new Map();
    jobRows.forEach((r) => {
        if (!WAGE_TYPES.includes(Number(r.pt))) return;
        const c = String(r.craft || '');
        const cur = m.get(c) || { craft: c, hrs: 0, stHrs: 0, otHrs: 0, gross: 0, basePay: 0, baseHrs: 0 };
        const hrs = Number(r.hrs) || 0;
        cur.hrs += hrs;
        if (Number(r.pt) === 1) { cur.stHrs += hrs; cur.basePay += Number(r.pay) || 0; cur.baseHrs += hrs; } else cur.otHrs += hrs;
        cur.gross += Number(r.gross) || 0;
        m.set(c, cur);
    });
    return [...m.values()].map((x) => ({ ...x, rate: x.baseHrs > 0 ? x.basePay / x.baseHrs : null }))
        .sort((a, b) => b.hrs - a.hrs || a.craft.localeCompare(b.craft));
}

/**
 * The progression table: one row per craft, its company-wide base rate in each
 * year, the change from the first year to the last, and the job's own rate.
 * crafts: the codes to show, in order. A year with fewer than MIN_HRS hours for
 * the craft shows no rate.
 */
export function progressionRows(rates, codes, jobByCraft = new Map()) {
    const ys = rates.years;
    return codes.map((c) => {
        const byYear = ys.map((y) => ({ yr: y, rate: rates.yearRate(c, y), hrs: rates.yearHours(c, y) }));
        const rated = byYear.filter((x) => x.rate != null);
        const first = rated[0];
        const lastR = rated[rated.length - 1];
        const span = first && lastR ? lastR.yr - first.yr : 0;
        const change = span > 0 ? lastR.rate / first.rate - 1 : null;
        const perYear = span > 0 ? (lastR.rate / first.rate) ** (1 / span) - 1 : null;
        const j = jobByCraft.get(c);
        return { craft: c, byYear, change, perYear, firstYr: first?.yr ?? null, lastYr: lastR?.yr ?? null, jobHrs: j?.hrs || 0, jobRate: j?.rate ?? null };
    });
}

/**
 * What this job's crew mix costs an hour in each year: the job's regular hours
 * per craft × each year's company rate, over the crafts rated in every year
 * shown (so a craft that drops in and out does not move the line).
 */
export function mixRateByYear(rates, jobCraftRows) {
    const ys = rates.years;
    const usable = jobCraftRows.filter((j) => j.craft && j.hrs > 0 && ys.every((y) => rates.yearRate(j.craft, y) != null));
    const w = usable.reduce((s, j) => s + j.hrs, 0);
    const allHrs = jobCraftRows.reduce((s, j) => s + j.hrs, 0);
    return {
        coverage: allHrs > 0 ? w / allHrs : 0,
        byYear: ys.map((y) => ({ yr: y, rate: w > 0 ? usable.reduce((s, j) => s + j.hrs * rates.yearRate(j.craft, y), 0) / w : null })),
    };
}

/**
 * The job's wages re-priced at year B: { base, actual, atBase, ratio, etcRatio,
 * byCraft: [{ craft, gross, atBase, src }], fallbackShare }.
 *   ratio     wages at B ÷ wages paid, over everything worked to date
 *   etcRatio  the same for the latest quarter's rates on the job's craft mix —
 *             what still-to-be-worked hours would save at year B's rates
 */
export function repriceAt(rates, jobRows, base) {
    let actual = 0; let atBase = 0; let fallback = 0;
    const byCraft = new Map();
    jobRows.forEach((r) => {
        if (!WAGE_TYPES.includes(Number(r.pt))) return;
        const g = Number(r.gross) || 0;
        if (!g) return;
        const c = String(r.craft || '');
        const { f, src } = rates.factor(c, base, Number(r.yr), Number(r.q));
        actual += g; atBase += g * f;
        if (src !== 'craft') fallback += g;
        const cur = byCraft.get(c) || { craft: c, gross: 0, atBase: 0, fallbackGross: 0 };
        cur.gross += g; cur.atBase += g * f;
        if (src !== 'craft') cur.fallbackGross += g;
        byCraft.set(c, cur);
    });
    // Hours still to work are paid at today's rates: price the job's craft mix at
    // the latest quarter on file.
    let etcNow = 0; let etcBase = 0;
    if (rates.last) {
        byCraft.forEach((x) => {
            const { f } = rates.factor(x.craft, base, rates.last.yr, rates.last.q);
            etcNow += x.gross; etcBase += x.gross * f;
        });
    }
    return {
        base,
        actual,
        atBase,
        ratio: actual > 0 ? atBase / actual : 1,
        etcRatio: etcNow > 0 ? etcBase / etcNow : 1,
        fallbackShare: actual > 0 ? fallback / actual : 0,
        byCraft: [...byCraft.values()].map((x) => ({ ...x, src: x.fallbackGross > x.gross / 2 ? 'company' : 'craft' }))
            .sort((a, b) => b.gross - a.gross),
    };
}

/**
 * The job's labor cost to date from the version's cost detail, by what moves
 * with the wage: wages (51100 regular, 51200 overtime), fringe (51300 payroll
 * tax and fringe, a share of wages), and everything else in 51xxx (per diem,
 * truck pay, ops support).
 */
export function laborToDate(costRows = []) {
    let wages = 0; let fringe = 0; let total = 0;
    (costRows || []).forEach((r) => {
        const code = String(r.Cost_Type_Code || '');
        if (code < '51000' || code > '51999') return;
        const v = Number(r.Job_Cost) || 0;
        total += v;
        if (code === '51100' || code === '51200') wages += v;
        else if (code === '51300') fringe += v;
    });
    return { wages, fringe, total, fringeRate: wages > 0 ? fringe / wages : 0 };
}

/**
 * Gross margin at actual rates, and at each earlier year's rates.
 *   totals   { revenue, totalCost, overhead, laborEac, billMultiplier } from the
 *            side menu; billMultiplier > 0 on a cost-plus job, where labor bills
 *            at the wage paid × the multiplier, so revenue moves with the wage
 *   labor    laborToDate(...)
 *   reprices [repriceAt(...)] one per year
 * Each scenario: { base, jtdSaving, etcSaving, saving, revenueChange, revenue,
 *                  gm, gmPct, ptsChange }.
 *   jtdSaving  wages to date × (1 − ratio) × (1 + fringe rate)
 *   etcSaving  the labor still to come (labor EAC − labor to date), the share of
 *              it that is wage and fringe as it is to date, × (1 − etcRatio)
 *   revenueChange  cost-plus only: −(wages saved) × the multiplier
 * A saving is positive when the earlier year's rates were lower. Overhead is
 * held as it is: Fusion allocates it on revenue, which a cost-plus scenario
 * moves a little, and that second-order change is left out.
 */
export function marginScenarios(totals, labor, reprices) {
    const revenue = Number(totals?.revenue) || 0;
    const mult = Math.max(0, Number(totals?.billMultiplier) || 0);
    const gm = revenue - (Number(totals?.totalCost) || 0) - (Number(totals?.overhead) || 0);
    const gmPct = revenue ? gm / revenue : null;
    const remaining = Math.max(0, (Number(totals?.laborEac) || 0) - labor.total);
    const remainingWages = labor.total > 0 ? remaining * labor.wages / labor.total : 0;
    const remainingFringe = labor.total > 0 ? remaining * labor.fringe / labor.total : 0;
    const remainingWage = remainingWages + remainingFringe;
    const actual = { revenue, gm, gmPct, remaining, remainingWage };
    const scenarios = reprices.map((p) => {
        const jtdWages = labor.wages * (1 - p.ratio);
        const etcWages = remainingWages * (1 - p.etcRatio);
        const jtdSaving = jtdWages * (1 + labor.fringeRate);
        const etcSaving = remainingWage * (1 - p.etcRatio);
        const saving = jtdSaving + etcSaving;
        const revenueChange = mult ? -(jtdWages + etcWages) * mult : 0;
        const rev = revenue + revenueChange;
        const g = gm + saving + revenueChange;
        const pct = rev ? g / rev : null;
        return {
            base: p.base, ratio: p.ratio, etcRatio: p.etcRatio, jtdSaving, etcSaving, saving, revenueChange, revenue: rev,
            gm: g, gmPct: pct, ptsChange: pct != null && gmPct != null ? (pct - gmPct) * 100 : null, fallbackShare: p.fallbackShare,
        };
    });
    return { actual, scenarios };
}

/** The years to compare against: every year on file before the latest one. */
export const baseYearsOf = (rates) => rates.years.filter((y) => rates.last && y < rates.last.yr);
